import React from "react";
import { Car, X, Plus, ArrowLeftRight, CheckCircle2, Banknote } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "../../ui";
import { Button } from "../../ui";
import { TradeVehicleForm } from "../lists/TradeVehicleForm";
import { formatCurrency } from "../../../utils/formatters";
import { Brand, Model, VehicleOption } from "../../../types";
import { cn } from "../../../utils/cn";

interface TradeVehicleData {
  brandId: string;
  modelId: string;
  versionId?: string;
  condition: string;
  modelYear: number;
  manufactureYear: number;
  mileage: number;
  fuelType: string;
  transmission: string;
  color: string;
  offeredValue: number;
  plate?: string;
  chassis?: string;
  renavam?: string;
  expenses?: Array<{ name: string; value: number }>;
  isAlienated?: boolean;
  alienatedDebt?: number;
}

interface TradeVehiclesSectionProps {
  tradeVehicles: TradeVehicleData[] | null;
  savedTradeVehicles: TradeVehicleData[] | null;
  brands: Brand[];
  models: Model[];
  vehicleOptions: any;
  onTradeVehiclesChange: (vehicles: TradeVehicleData[] | null) => void;
  onSavedTradeVehiclesChange: (vehicles: TradeVehicleData[] | null) => void;
}

const isTradeEnabled = (
  tradeVehicles: TradeVehicleData[] | null,
  savedTradeVehicles: TradeVehicleData[] | null
) =>
  (tradeVehicles && tradeVehicles.length > 0) ||
  (savedTradeVehicles && savedTradeVehicles.length > 0);

// ── Toggle switch ─────────────────────────────────────────────────────────────
const ToggleSwitch = ({
  checked,
  onChange,
  label,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  id: string;
}) => (
  <label
    htmlFor={id}
    className="flex items-center gap-2.5 cursor-pointer select-none group"
  >
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200",
        checked ? "bg-accent" : "bg-border"
      )}
    >
      <span
        className={cn(
          "pointer-events-none block h-4 w-4 rounded-full bg-white shadow-sm ring-0 transition-transform duration-200",
          checked ? "translate-x-4" : "translate-x-0"
        )}
      />
    </button>
    <span className="text-sm font-medium text-foreground group-hover:text-accent transition-colors">
      {label}
    </span>
  </label>
);

// ── Saved vehicle summary card ────────────────────────────────────────────────
const SavedVehicleCard = ({
  vehicle,
  brands,
  models,
  onRemove,
}: {
  vehicle: TradeVehicleData;
  brands: Brand[];
  models: Model[];
  onRemove: () => void;
}) => {
  const brand = brands.find((b) => b.id === vehicle.brandId);
  const model = models.find((m) => m.id === vehicle.modelId);

  const vehicleName =
    brand && model
      ? `${brand.name} ${model.name}${vehicle.modelYear ? ` ${vehicle.modelYear}` : ""}`
      : "Veículo sem informações";

  const expensesTotal =
    vehicle.expenses?.reduce((sum, e) => sum + e.value, 0) || 0;
  const alienatedDebt = vehicle.isAlienated ? (vehicle.alienatedDebt || 0) : 0;
  const netValue = (vehicle.offeredValue || 0) - expensesTotal - alienatedDebt;

  const specs = [
    vehicle.condition === "used" ? "Usado" : "Novo",
    vehicle.mileage ? `${vehicle.mileage.toLocaleString("pt-BR")} km` : null,
    vehicle.plate || null,
  ].filter(Boolean);
  const isAlienated = vehicle.isAlienated && (vehicle.alienatedDebt || 0) > 0;

  return (
    <div className="flex items-start gap-4 p-4 bg-card border border-border rounded-lg hover:shadow-sm transition-shadow">
      {/* Vehicle icon */}
      <div className="w-12 h-12 bg-accent/10 rounded-lg flex-shrink-0 flex items-center justify-center">
        <Car className="h-6 w-6 text-accent" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h4 className="font-semibold text-foreground text-sm truncate">
              {vehicleName}
            </h4>
            {specs.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                {specs.map((spec, i) => (
                  <span
                    key={i}
                    className="text-xs text-muted-foreground bg-surface border border-border px-1.5 py-0.5 rounded"
                  >
                    {spec}
                  </span>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onRemove}
            className="flex-shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            title="Remover veículo"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Financial summary */}
        <div className="mt-3 pt-3 border-t border-border flex items-end justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span>
                Ofertado:{" "}
                <span className="font-semibold font-mono text-foreground">
                  {formatCurrency(vehicle.offeredValue || 0)}
                </span>
              </span>
              {expensesTotal > 0 && (
                <span>
                  Despesas:{" "}
                  <span className="font-semibold font-mono text-destructive">
                    − {formatCurrency(expensesTotal)}
                  </span>
                </span>
              )}
              {isAlienated && (
                <span>
                  Dívida:{" "}
                  <span className="font-semibold font-mono text-destructive">
                    − {formatCurrency(vehicle.alienatedDebt || 0)}
                  </span>
                </span>
              )}
            </div>
            {isAlienated && (
              <div className="flex items-center gap-1.5">
                <Banknote className="h-3 w-3 text-amber-600" />
                <span className="text-xs font-semibold text-amber-700">Alienado</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-accent" />
            <span className="text-sm font-bold font-mono text-accent">
              {formatCurrency(netValue)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main section ──────────────────────────────────────────────────────────────
export const TradeVehiclesSection: React.FC<TradeVehiclesSectionProps> = ({
  tradeVehicles,
  savedTradeVehicles,
  brands,
  models,
  onTradeVehiclesChange,
  onSavedTradeVehiclesChange,
}) => {
  const enabled = isTradeEnabled(tradeVehicles, savedTradeVehicles);

  const handleToggle = (checked: boolean) => {
    if (checked) {
      onTradeVehiclesChange([
        {
          brandId: "",
          modelId: "",
          condition: "used",
          modelYear: new Date().getFullYear(),
          manufactureYear: new Date().getFullYear(),
          mileage: 0,
          fuelType: "",
          transmission: "",
          color: "",
          offeredValue: 0,
          expenses: [],
        },
      ]);
    } else {
      onTradeVehiclesChange(null);
      onSavedTradeVehiclesChange(null);
    }
  };

  const handleRemoveSaved = (index: number) => {
    if (!savedTradeVehicles) return;
    const updated = savedTradeVehicles.filter((_, i) => i !== index);
    onSavedTradeVehiclesChange(updated.length > 0 ? updated : null);
  };

  const handleRemoveEditing = (index: number) => {
    if (!tradeVehicles) return;
    const updated = tradeVehicles.filter((_, i) => i !== index);
    onTradeVehiclesChange(updated.length > 0 ? updated : null);
  };

  const handleSaveVehicle = (index: number, vehicleData: TradeVehicleData) => {
    onSavedTradeVehiclesChange([...(savedTradeVehicles || []), vehicleData]);
    if (!tradeVehicles) return;
    const updated = tradeVehicles.filter((_, i) => i !== index);
    onTradeVehiclesChange(updated.length > 0 ? updated : null);
  };

  const handleAddVehicle = () => {
    onTradeVehiclesChange([
      ...(tradeVehicles || []),
      {
        brandId: "",
        modelId: "",
        condition: "used",
        modelYear: new Date().getFullYear(),
        manufactureYear: new Date().getFullYear(),
        mileage: 0,
        fuelType: "",
        transmission: "",
        color: "",
        offeredValue: 0,
        expenses: [],
        isAlienated: false,
        alienatedDebt: 0,
      },
    ]);
  };

  const totalSavedValue =
    savedTradeVehicles?.reduce((sum, v) => {
      const expenses = v.expenses?.reduce((s, e) => s + e.value, 0) || 0;
      const debt = v.isAlienated ? (v.alienatedDebt || 0) : 0;
      return sum + (v.offeredValue || 0) - expenses - debt;
    }, 0) || 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-accent" />
            Veículos de Troca
          </CardTitle>

          <ToggleSwitch
            id="hasTradeVehicles"
            checked={!!enabled}
            onChange={handleToggle}
            label="Cliente quer dar na troca"
          />
        </div>
      </CardHeader>

      {enabled && (
        <CardContent className="space-y-5">

          {/* ── Veículos salvos ────────────────────────────────────────── */}
          {savedTradeVehicles && savedTradeVehicles.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Veículos confirmados
              </p>
              {savedTradeVehicles.map((vehicle, index) => (
                <SavedVehicleCard
                  key={`saved-${index}`}
                  vehicle={vehicle}
                  brands={brands}
                  models={models}
                  onRemove={() => handleRemoveSaved(index)}
                />
              ))}

              {/* Total geral (quando há mais de 1 veículo salvo) */}
              {savedTradeVehicles.length > 1 && (
                <div className="flex items-center justify-between px-4 py-2.5 bg-accent/5 border border-accent/20 rounded-lg">
                  <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Total de troca
                  </span>
                  <span className="text-sm font-bold font-mono text-accent">
                    {formatCurrency(totalSavedValue)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Formulários em edição ──────────────────────────────────── */}
          {tradeVehicles && tradeVehicles.length > 0 && (
            <div className="space-y-4">
              {tradeVehicles.length > 1 && (
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Preenchendo dados
                </p>
              )}

              {tradeVehicles.map((vehicle, index) => (
                <div
                  key={`editing-${index}`}
                  className="rounded-lg border border-border bg-surface overflow-hidden"
                >
                  {/* Form header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
                    <div className="flex items-center gap-2">
                      <Car className="h-4 w-4 text-accent" />
                      <span className="text-sm font-semibold text-foreground">
                        {tradeVehicles.length > 1
                          ? `Veículo ${(savedTradeVehicles?.length || 0) + index + 1}`
                          : "Dados do Veículo"}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveEditing(index)}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      title="Descartar veículo"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Form body */}
                  <div className="p-5">
                    <TradeVehicleForm
                      value={vehicle}
                      onChange={(data) => {
                        if (data && tradeVehicles) {
                          const updated = [...tradeVehicles];
                          updated[index] = data;
                          onTradeVehiclesChange(updated);
                        }
                      }}
                      onSave={(vehicleData) => handleSaveVehicle(index, vehicleData)}
                      showSaveButton
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Adicionar outro veículo ────────────────────────────────── */}
          {/* Only show "add" when no form is open OR when there are already saved vehicles */}
          {(!tradeVehicles || tradeVehicles.length === 0) && (
            <Button
              type="button"
              variant="outline"
              onClick={handleAddVehicle}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              {savedTradeVehicles && savedTradeVehicles.length > 0
                ? "Adicionar outro veículo"
                : "Preencher dados do veículo"}
            </Button>
          )}

          {/* Add another while form is still open */}
          {tradeVehicles && tradeVehicles.length > 0 && (
            <button
              type="button"
              onClick={handleAddVehicle}
              className="flex items-center gap-1.5 text-xs font-semibold text-accent hover:text-accent/80 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Incluir mais um veículo
            </button>
          )}
        </CardContent>
      )}

      {/* Disabled state — subtle hint */}
      {!enabled && (
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-2">
            Ative a opção acima para registrar veículos de troca nesta proposta.
          </p>
        </CardContent>
      )}
    </Card>
  );
};
